namespace Solovis.Reports.Parts.Tables {

  export interface TableAsChartControlOptions extends Solovis.UI.BaseControlOptions {
    tableControl: DataTableControl;

    chartState: TableAsChartState;

    showAddChartOptionsOnStartup?: boolean;
  }

  export interface TableAsChartState {
    seriesExportList?: DataTableSeriesExportConfiguration[];
    chartSettings?: any;
    panes?: any;
    argument?: any;

    /**
     * Currently, chartType can be "chart" or "pie".
     */
    chartType?: string;

    data?: { sortByFirstSeriesValue?: "asc" | "desc"; firstNDataRows?: number };

    chartWidth?: number;
    chartHeight?: number;
    titleText?: string;
    exportEnabled?: boolean;
    animationEnabled?: boolean;
    rotated?: boolean;

    legend?: any;
    argumentAxis?: any;
    valueAxisList?: any;
    tooltip?: any;
    crosshair?: any;
    pointPalette?: any;
    customizePoint?: TableAsChartCustomizePointState;
    customizeLabel?: TableAsChartCustomizeLabelState;

    barGroupPadding?: string;
  }


  export interface TableAsChartCustomizePointState {
    /**
     * Custom function for determining point/bar color.
     */
    color?: string;
  }

  export interface TableAsChartCustomizeLabelState {
    /**
     * Custom function for determining visibility of the label.
     */
    visible?: string;

    /**
     * Custom function for determining label text.
     */
    text?: string;

    /**
     * Custom function for determining label background color.
     */
    backgroundColor?: string;
  }


  export class TableAsChartControl extends Solovis.UI.BaseControl {

    public tableControl: DataTableControl;
    private newControlsSupported = false;

    public _chartState: TableAsChartState;
    public _chartContainer: JQuery;
    public _backRegion: JQuery;
    public _backText: JQuery;
    public _backButton: JQuery;

    public _optionsDialog: any;
    public _previewChartState: TableAsChartState;

    /**
     * _seriesDataUsed is the series information for constructing the current view of the table.
     */
    public _seriesDataUsed: any;

    public filterGroupKeys: Array<string>;
    public drillDownOrder: Array<{ argumentName: string, groupKey: string }>;

    static AllPrefix: string = "all::";

    static NewUXFontSettings = {
      color: "#3D3D3D",
      family: "Roboto, sans-serif",
      size: "11px",
      weight: "400",
    }

    constructor(options: TableAsChartControlOptions) {
      super(options);

      this.tableControl = options.tableControl;
      this._chartState = options.chartState;
      this.filterGroupKeys = [];
      this.drillDownOrder = [];
    }

    public doRender() {
      this.newControlsSupported = Solovis.UX.Utils.areNewControlsSupported();
      if (this.newControlsSupported) {
        this.container.parent().addClass("paux");
        this.container.addClass("slvs-chart");
        const pageViewModel = (window as any).PageViewModel;
        if (!pageViewModel.isDashboard) {
          this.container.addClass("slvs-panel tw-p-2.5");
        }
      }
      let self = this;
      this._chartContainer = $("<div>").appendTo(this.container);
      // NOTE: region for back button when chart is drilled into to show bread crumbs and back button
      this._backRegion = $("<div>").appendTo(this.container).css({ "display": "flex", "align-items": "center" });
      this._backText = $("<p>").appendTo(this._backRegion).addClass("dx-widget"); //line text class up with other things in there
      this._backButton = $("<div>").appendTo(this._backRegion);

      this.bindEventUntilControlDestroyedOrNoLongerPartOfDom(this.tableControl, "dataViewUpdated", function () {
        self._updateChart();
      });

      this._backButton.attr('id', 'chart-backButton');
      this._backButton.dxButton({
        icon: "chevronleft",
        visible: false
      }).dxButton("instance");

      if (this.tableControl.dataView && this._chartState) {
        this._updateChart();
      }

      $(this.container).bind("contextmenu", function (e) {
        e.stopPropagation();

        if (DataTableControl.canCurrentUserConfigureTableCharts()) {
          self._showContextMenu(e);
        }

        return false;
      });

      if ((this.options as TableAsChartControlOptions).showAddChartOptionsOnStartup) {
        setTimeout(
          function () {
            self.showAddChartDialog();
          },
          100);
      }
    }

    public async _updateChart() {
      let self = this;

      try {
        // TODO: Do we need to do more to cleanup chart.
        $(this._chartContainer).remove();
        this._chartContainer = $("<div>").appendTo(this.container);

        let chartStateToUse: TableAsChartState;
        if (this._previewChartState) {
          chartStateToUse = this._previewChartState;
        }
        else {
          chartStateToUse = this._chartState;
        }

        let seriesData = Solovis.Reports.Parts.Tables.DataTableSeriesProcessor.exportSeriesData({
          tableControl: this.tableControl,
          seriesExportList: chartStateToUse.seriesExportList,
          firstNDataRows: chartStateToUse.data ? chartStateToUse.data.firstNDataRows : null,
          argument: chartStateToUse.argument,
          sortByFirstSeriesValue: chartStateToUse.data ? chartStateToUse.data.sortByFirstSeriesValue : null
        });
        this._seriesDataUsed = seriesData;

        let chartSettingsToUse: any = {
          commonSeriesSettings: {
            argumentField: seriesData.argumentField
          },
          dataSource: seriesData.dataEntries,
          argument: chartStateToUse.argument,
        };

        this._applySeriesData(chartStateToUse, chartSettingsToUse, seriesData);

        if (chartStateToUse.chartWidth || chartStateToUse.chartHeight) {
          if (!chartSettingsToUse.size) {
            chartSettingsToUse.size = {};
          }

          if (chartStateToUse.chartWidth) {
            chartSettingsToUse.size.width = chartStateToUse.chartWidth;
          }

          if (chartStateToUse.chartHeight) {
            chartSettingsToUse.size.height = chartStateToUse.chartHeight;
          }
        }

        if (chartStateToUse.titleText) {
          if (!chartSettingsToUse.title) {
            chartSettingsToUse.title = {};
          }

          chartSettingsToUse.title.text = this._evaluateCustomTextFunction(chartStateToUse.titleText, true);
        }

        if (chartStateToUse.exportEnabled) {
          if (!chartSettingsToUse.export) {
            chartSettingsToUse.export = {};
          }

          chartSettingsToUse.export.enabled = true;
        }

        if (Solovis.isNotNull(chartStateToUse.animationEnabled)) {
          if (!chartSettingsToUse.animation) {
            chartSettingsToUse.animation = {};
          }

          chartSettingsToUse.animation.enabled = chartStateToUse.animationEnabled;
        }

        if (Solovis.isNotNull(chartStateToUse.rotated)) {
          chartSettingsToUse.rotated = chartStateToUse.rotated;
        }

        if (chartStateToUse.legend) {
          chartSettingsToUse.legend = chartStateToUse.legend;
        }
        if (this.newControlsSupported) {
          chartSettingsToUse.legend.font = TableAsChartControl.NewUXFontSettings;
        }

        if (Solovis.isNotNull(chartStateToUse.barGroupPadding)) {
          try {
            chartSettingsToUse.barGroupPadding = parseFloat(chartStateToUse.barGroupPadding);
          } catch (ex) {
            console.log("ERROR: TableAsChartControl: Unable to parse barGroupPadding: " + chartStateToUse.barGroupPadding);
          }
        }

        this._applyPanes(chartStateToUse, chartSettingsToUse, seriesData);

        this._applyArgumentAxis(chartStateToUse, chartSettingsToUse, seriesData);
        this._applyValueAxisList(chartStateToUse, chartSettingsToUse, seriesData);

        this._applyTooltip(chartStateToUse, chartSettingsToUse, seriesData);
        this._applyCrosshair(chartStateToUse, chartSettingsToUse);
        await this._applyCustomizePoint(chartStateToUse, chartSettingsToUse);
        this._applyCustomizeLabel(chartStateToUse, chartSettingsToUse);

        this._applyClickHandlers(chartStateToUse, chartSettingsToUse);

        if (!chartStateToUse.chartType || chartStateToUse.chartType === "chart") {
          (this._chartContainer as any).dxChart(chartSettingsToUse);
          (this._chartContainer as any).dxChart("instance").render();
        }
        else if (chartStateToUse.chartType === "pie") {
          if (!chartSettingsToUse.type) {
            chartSettingsToUse.type = "donut";
          }

          (this._chartContainer as any).dxPieChart(chartSettingsToUse);
        }
        else if (chartStateToUse.chartType === "polar") {
          (this._chartContainer as any).dxPolarChart(chartSettingsToUse);
        }
        else {
          console.log("ERROR: TableAsChartControl: Unknown chartType: " + chartStateToUse.chartType);
        }
      }
      catch (ex) {
        console.log("ERROR: TableAsChartControl._updateChart failed.");
        console.log(ex);

        this._chartContainer.text("Please review chart configuration.");
      }
    }


    private _applySeriesData(chartStateToUse: TableAsChartState, chartSettingsToUse: any, seriesData: DataTableSeriesExportedData) {
      let seriesListToUse = [];

      if (seriesData.seriesList) {
        for (let seriesEntry of seriesData.seriesList) {
          // We create a copy so we can manipulate things like the label format.
          let entryCopy: any = {};

          for (let key in seriesEntry) {
            if (seriesEntry.hasOwnProperty(key)) {
              let value = seriesEntry[key];

              if (key === "label") {
                // We will modify label so we copy it to avoid making changes to base configuration.
                value = Solovis.deepCopy(value);
              }

              entryCopy[key] = value;
            }
          }

          let tryToAutoSetFormat = false;
          if (!entryCopy.label || !entryCopy.label.format || !entryCopy.label.format.type) {
            tryToAutoSetFormat = true;
          }

          if (tryToAutoSetFormat) {
            let colForValue: DataTableHelpers.Column;

            if (seriesEntry.valueField) {
              if (!_.isEmpty(seriesEntry._columnsAsArguments)) {
                colForValue = seriesEntry._columnsAsArguments[0];
              }
              else if (seriesEntry._valueColumn) {
                colForValue = seriesEntry._valueColumn;
              }
            }

            if (colForValue) {
              if (!entryCopy.label)
                entryCopy.label = {};
              if (!entryCopy.label.format)
                entryCopy.label.format = {};

              this._applyDefaultFormatOptionsForColumn(entryCopy.label.format, colForValue);
            }
          }

          seriesListToUse.push(entryCopy);
        }
      }

      chartSettingsToUse.series = seriesListToUse;
    }

    private _applyPanes(chartStateToUse: TableAsChartState, chartSettingsToUse: any, seriesData: DataTableSeriesExportedData) {
      // Determine which panes have at least one non-null value.
      let paneNamesWithDataSet: any = {};

      for (let seriesEntry of seriesData.seriesList) {
        if (seriesEntry.pane) {
          if (seriesEntry._hasNonNullValue) {
            if (seriesEntry.pane) {
              paneNamesWithDataSet[seriesEntry.pane] = true;
            }
            else {
              paneNamesWithDataSet["__pane_with_no_name"] = true;
            }
          }
        }
      }

      let panesToUse;
      if (chartStateToUse.panes && chartStateToUse.panes.length > 0) {
        panesToUse = [];

        // Only take other panes if they have data.
        for (let i = 0; i < chartStateToUse.panes.length; i++) {
          let sourcePane = chartStateToUse.panes[i];

          let paneKey = sourcePane.name ? sourcePane.name : "__pane_with_no_name";
          if (paneNamesWithDataSet[paneKey]) {
            panesToUse.push(Solovis.deepCopy(sourcePane));
          }
        }
      }
      else {
        panesToUse = [{}];
      }

      chartSettingsToUse.panes = panesToUse;
    }


    private _fixupLabelOverlappingBehavior(label) {
      if (label) {
        // See if we need to promote overlappingBehavior directly onto the label (that's where we store it, but
        // dxChart now needs it directly on the label as of 17.1).
        if (label.overlappingBehavior) {
          if (label.overlappingBehavior.mode) {
            let mode = label.overlappingBehavior.mode;
            let rotationAngle = label.overlappingBehavior.rotationAngle;
            let staggeringSpacing = label.overlappingBehavior.staggeringSpacing;

            label.overlappingBehavior = mode;
            label.rotationAngle = rotationAngle;
            label.staggeringSpacing = staggeringSpacing;
          }
          else if (typeof label.overlappingBehavior !== "string") {
            label.overlappingBehavior = null;
          }
        }
      }
    }


    private _applyArgumentAxis(chartStateToUse: TableAsChartState, chartSettingsToUse: any, seriesData: DataTableSeriesExportedData) {
      if (chartStateToUse.argumentAxis) {
        chartSettingsToUse.argumentAxis = Solovis.deepCopy(chartStateToUse.argumentAxis);
      }

      if (!chartSettingsToUse.argumentAxis) {
        chartSettingsToUse.argumentAxis = {};
      }

      if (chartSettingsToUse.argumentAxis.label) {
        this._fixupLabelOverlappingBehavior(chartSettingsToUse.argumentAxis.label);
      }

      let tryToAutoSetFormat = false;
      if (!chartSettingsToUse.argumentAxis.label || !chartSettingsToUse.argumentAxis.label.format || !chartSettingsToUse.argumentAxis.label.format.type) {
        tryToAutoSetFormat = true;
      }

      if (tryToAutoSetFormat) {
        // By default, base the format of the argument axis on the argument column.
        let col = seriesData.firstArgumentColumnEncountered;
        if (col) {
          if (!chartSettingsToUse.argumentAxis.label)
            chartSettingsToUse.argumentAxis.label = {};
          if (!chartSettingsToUse.argumentAxis.label.format)
            chartSettingsToUse.argumentAxis.label.format = {};

          this._applyDefaultFormatOptionsForColumn(chartSettingsToUse.argumentAxis.label.format, col);
        }
      }
      if (this.newControlsSupported) {
        chartSettingsToUse.argumentAxis.label.font = TableAsChartControl.NewUXFontSettings;
      }
    }

    private _applyValueAxisList(chartStateToUse: TableAsChartState, chartSettingsToUse: any, seriesData: DataTableSeriesExportedData) {
      if (chartStateToUse.valueAxisList) {
        chartSettingsToUse.valueAxis = Solovis.deepCopy(chartStateToUse.valueAxisList);
      }

      if (!chartSettingsToUse.valueAxis) {
        chartSettingsToUse.valueAxis = [];
      }

      if (chartSettingsToUse.valueAxis.length === 0) {
        // Ensure we have at least one axis entry in the array.
        chartSettingsToUse.valueAxis.push({});
      }

      let axisIndex = 0;
      for (let axis of chartSettingsToUse.valueAxis) {
        let tryToAutoSetFormat = false;
        if (!axis.label || !axis.label.format || !axis.label.format.type) {
          tryToAutoSetFormat = true;
        }

        if (!axis.tick) {
          axis.tick = {};
        }
        if (Solovis.isNull(axis.tick.visible)) {
          axis.tick.visible = false;
        }

        if (tryToAutoSetFormat) {
          let colForAxisValue: DataTableHelpers.Column;

          for (let seriesEntry of seriesData.seriesList) {
            if (seriesEntry.valueField) {
              if ((Solovis.isNotNull(seriesEntry.axis) && seriesEntry.axis === axis.name)
                || (axisIndex == 0 && !seriesEntry.axis)) {
                // Axis names match or this is the first axis and axis is not specified.
                if (!_.isEmpty(seriesEntry._columnsAsArguments)) {
                  colForAxisValue = seriesEntry._columnsAsArguments[0];
                  break;
                }
                else if (seriesEntry._valueColumn) {
                  colForAxisValue = seriesEntry._valueColumn;
                  break;
                }
              }
            }
          }

          if (colForAxisValue) {
            if (!axis.label)
              axis.label = {};
            if (!axis.label.format)
              axis.label.format = {};

            this._applyDefaultFormatOptionsForColumn(axis.label.format, colForAxisValue);
          }
        }
        if (this.newControlsSupported) {
          axis.label.font = TableAsChartControl.NewUXFontSettings;
        }

        axisIndex++;
      }
    }

    private _applyDefaultFormatOptionsForColumn(format: any, col: DataTableHelpers.Column) {
      if (!col)
        return;

      if (col.dataType === "dayId") {
        format.formatter = function (value) {
          if (value instanceof Date) {
            return kendo.toString(value, "MMM d, yyyy");
          }
          else if (typeof value === "number") {
            return kendo.toString(new Date(value), "MMM d, yyyy");
          }
        };
      }
      else if (col.dataType === "percentage") {
        format.type = "percent";
        if (Solovis.isNull(format.precision)) {
          format.precision = 2;
        }
      }
      else if (col.dataType === "number" || col.dataType === "currency") {
        format.type = "fixedPoint";
      }
    }

    private _applyTooltip(chartStateToUse: TableAsChartState, chartSettingsToUse: any, seriesData: DataTableSeriesExportedData) {
      let self = this;

      if (chartStateToUse.tooltip) {
        if (!chartSettingsToUse.tooltip) {
          chartSettingsToUse.tooltip = {};
        }
        chartSettingsToUse.tooltip.enabled = chartStateToUse.tooltip.enabled;
        chartSettingsToUse.tooltip.shared = chartStateToUse.tooltip.shared;
        chartSettingsToUse.tooltip.border = chartStateToUse.tooltip.border;
        chartSettingsToUse.tooltip.format = chartStateToUse.tooltip.format;
        chartSettingsToUse.tooltip.argumentFormat = chartStateToUse.tooltip.argumentFormat;

        // See if we should auto set argumentFormat.
        let tryToAutoSetArgumentFormat = false;
        if (!chartSettingsToUse.tooltip.argumentFormat || !chartSettingsToUse.tooltip.argumentFormat.type) {
          tryToAutoSetArgumentFormat = true;
        }

        if (tryToAutoSetArgumentFormat) {
          // By default, base the format of the argument axis on the argument column.
          let col = seriesData.firstArgumentColumnEncountered;
          if (col) {
            if (!chartSettingsToUse.tooltip.argumentFormat)
              chartSettingsToUse.tooltip.argumentFormat = {};

            this._applyDefaultFormatOptionsForColumn(chartSettingsToUse.tooltip.argumentFormat, col);
          }
        }

        // See if we should try to set value format.
        let tryToAutoSetValueFormat = false;
        if (!chartSettingsToUse.tooltip.format || !chartSettingsToUse.tooltip.format.type) {
          tryToAutoSetValueFormat = true;
        }

        if (tryToAutoSetValueFormat) {
          // By default, base the format of the argument axis on the argument column.
          for (let seriesEntry of seriesData.seriesList) {
            let col;
            if (!_.isEmpty(seriesEntry._columnsAsArguments)) {
              col = seriesEntry._columnsAsArguments[0];
            }
            else if (seriesEntry._valueColumn) {
              col = seriesEntry._valueColumn;
            }

            if (col) {
              if (!chartSettingsToUse.tooltip.format)
                chartSettingsToUse.tooltip.format = {};

              this._applyDefaultFormatOptionsForColumn(chartSettingsToUse.tooltip.format, col);
              break;
            }
          }
        }

        if (chartStateToUse.tooltip.contentToShow === "argumentAndValue") {
          chartSettingsToUse.tooltip.customizeTooltip = function () {
            return {
              text: this.argumentText + ": " + this.valueText
            };
          };
        }
        else if (chartStateToUse.tooltip.contentToShow === "customText") {
          chartSettingsToUse.tooltip.customizeTooltip = function () {
            let values: any = {
              argument: this.argument,
              argumentText: this.argumentText,
              seriesName: this.seriesName,
              value: this.value,
              valueText: this.valueText
            };

            if (this.point) {
              values.tag = this.point.tag;
            }

            let tooltipValue: string = self._evaluateCustomTextFunction(chartStateToUse.tooltip.customText, false, values);
            if (tooltipValue) {
              let tooltipDiv = $("<div>");

              let tooltipParts = tooltipValue.split("||");
              for (let part of tooltipParts) {
                $("<div>").text(part).appendTo(tooltipDiv);
              }

              return { html: tooltipDiv.html() };
            }
            else {
              return null;
            }
          };
        }
        if (this.newControlsSupported) {
          chartSettingsToUse.tooltip.font = TableAsChartControl.NewUXFontSettings;
        }
      }
    }

    private _applyCrosshair(chartStateToUse: TableAsChartState, chartSettingsToUse: any) {
      if (chartStateToUse.crosshair) {
        if (!chartSettingsToUse.crosshair) {
          chartSettingsToUse.crosshair = {};
        }
        if (!chartSettingsToUse.crosshair.label) {
          chartSettingsToUse.crosshair.label = {};
        }

        chartSettingsToUse.crosshair.enabled = chartStateToUse.crosshair.enabled;
        chartSettingsToUse.crosshair.width = chartStateToUse.crosshair.width;
        chartSettingsToUse.crosshair.color = chartStateToUse.crosshair.color;
        chartSettingsToUse.crosshair.dashStyle = chartStateToUse.crosshair.dashStyle;
        chartSettingsToUse.crosshair.label.visible = chartStateToUse.crosshair.labelVisible;
        chartSettingsToUse.crosshair.label.backgroundColor = chartStateToUse.crosshair.labelBackgroundColor;
      }
    }

    private _applyCustomizePoint(chartStateToUse: TableAsChartState, chartSettingsToUse: any) {
      let self = this;
      let pointPalette;

      let customizePoint = chartStateToUse.customizePoint;

      if (chartStateToUse.pointPalette && chartStateToUse.pointPalette.entries && chartStateToUse.pointPalette.entries.length > 0) {
        pointPalette = chartStateToUse.pointPalette;
      }
      else {
        if ((window as any).Solovis) {
          let solovisAny = (window as any).Solovis as any;
          if (solovisAny && solovisAny.Custom && solovisAny.Custom.ColorPalette) {
            pointPalette = solovisAny.Custom.ColorPalette;
          }
        }
      }

      let pointPaletteProcessor: PointPaletteProcessor;
      if (pointPalette) {
        pointPaletteProcessor = new PointPaletteProcessor({
          tableControl: self.tableControl,
          palette: pointPalette,
          chartType: self._chartState.chartType
        });
      }

      // First we see if we need to override the color of an of the series.
      if (pointPaletteProcessor && chartSettingsToUse.series) {
        chartSettingsToUse.series
          .filter(item => item.name)
          .forEach(seriesEntry => {
            if (!seriesEntry.color) {
              seriesEntry.color = pointPaletteProcessor.checkForColor({
                seriesName: seriesEntry.name,
                seriesColorCheck: true
              });
            } else {
              pointPaletteProcessor.addToMatchList(seriesEntry.color, seriesEntry.name);
            }
          });
      }

      if (customizePoint || pointPalette) {
        chartSettingsToUse.customizePoint = function () {
          let customPoint: any = {};

          if (customizePoint || pointPalette) {
            let values = {
              argument: this.argument,
              value: this.value,
              seriesName: this.seriesName,
              tag: this.tag,
              index: this.index,
              rangeValue1: this.rangeValue1,
              rangeValue2: this.rangeValue2
            };

            if (customizePoint && customizePoint.color) {
              let colorValue: string = self._evaluateCustomTextFunction(customizePoint.color, false, values);
              if (colorValue) {
                customPoint.color = colorValue;
              }
            }

            if (!customPoint.color && pointPaletteProcessor) {
              // Fallback to the specified palette if no custom value provided.
              customPoint.color = pointPaletteProcessor.checkForColor(values);
            }
          }

          return customPoint;
        }
      }
    }

    private _applyCustomizeLabel(chartStateToUse: TableAsChartState, chartSettingsToUse: any) {
      let self = this;

      if (chartStateToUse.customizeLabel) {
        chartSettingsToUse.customizeLabel = function () {
          let customLabel: any = {};

          let values = {
            argument: this.argument,
            value: this.value,
            seriesName: this.seriesName,
            tag: this.tag,
            index: this.index,
            rangeValue1: this.rangeValue1,
            rangeValue2: this.rangeValue2
          };

          if (chartStateToUse.customizeLabel.visible) {
            let visibleValue: any = self._evaluateCustomFunction(chartStateToUse.customizeLabel.visible, false, values);
            if (Solovis.isNotNull(visibleValue)) {
              customLabel.visible = visibleValue;
            }
          }

          if (chartStateToUse.customizeLabel.text) {
            customLabel.customizeText = function () {
              let customizeTextValues = {
                argument: this.argument,
                value: this.value,
                seriesName: this.seriesName,
                tag: values.tag,
                index: values.index,
                rangeValue1: this.rangeValue1,
                rangeValue2: this.rangeValue2,
                argumentText: this.argumentText,
                valueText: this.valueText,
              };

              let textValue: string = self._evaluateCustomTextFunction(chartStateToUse.customizeLabel.text, false, customizeTextValues);
              return textValue;
            };
          }

          if (chartStateToUse.customizeLabel.backgroundColor) {
            let bgColorValue: string = self._evaluateCustomTextFunction(chartStateToUse.customizeLabel.backgroundColor, false, values);
            if (bgColorValue) {
              customLabel.backgroundColor = bgColorValue;
            }
          }

          return customLabel;
        }
      }
    }


    private _applyClickHandlers(chartStateToUse: TableAsChartState, chartSettingsToUse: any) {
      let self = this;
      const container = self.container;

      if (Boolean((Solovis as any).Info.userEligibleForChartDrillDown) &&
        self.tableControl.dataView.flattenedRows.filter((item) => item.expanded === false).length > 0
      ) {
        self._addPointerCursor(container);
      }

      chartSettingsToUse.onPointClick = function (e) {
        let target = e.target;
        if (target) {
          let valuesContext: any = {
            eventType: "pointClick",
            argument: target.argument,
            groupKey: target.data.groupKey,
            value: target.value,
            tag: target.tag,
            seriesName: target.series ? target.series.name : null,
            seriesType: target.series ? target.series.type : null,
            seriesIndex: target.series ? target.series.index : null,
          };

          valuesContext.originalSeriesEntry = self._getOriginalSeriesEntryForSeriesIndex(valuesContext.seriesIndex);
          valuesContext.originalSeriesExportIndex = valuesContext.originalSeriesEntry ? valuesContext.originalSeriesEntry._seriesExportIndex : null;
          valuesContext.originalSeriesExportConfig = self._getOriginalSeriesConfigForOriginalSeriesEntry(valuesContext.originalSeriesEntry);

          self._handlePointClick(valuesContext);
        }
      };
      chartSettingsToUse.onLegendClick = function (e) {
        let target = e.target;
        if (target) {
          let valuesContext: any = {
            eventType: "legendClick",
            tag: target.tag,
            seriesName: target.name,
            seriesType: target.type,
            seriesIndex: target.index
          };

          valuesContext.originalSeriesEntry = self._getOriginalSeriesEntryForSeriesIndex(valuesContext.seriesIndex);
          valuesContext.originalSeriesExportIndex = valuesContext.originalSeriesEntry ? valuesContext.originalSeriesEntry._seriesExportIndex : null;
          valuesContext.originalSeriesExportConfig = self._getOriginalSeriesConfigForOriginalSeriesEntry(valuesContext.originalSeriesEntry);

          /* Hiding/Showing the data on legend click. */
          let point = e.target;
          if (e.component.NAME === 'dxPieChart') {
            const arg = e.target;
            point = this.getAllSeries()[0].getPointsByArg(arg)[0];
          }

          const isVisible = typeof point.isVisible === 'function' && point.isVisible()
          const canShow = typeof point.show === 'function' && !isVisible
          const canHide = typeof point.hide === 'function' && isVisible

          if (canHide) {
            point.hide();
          } else if (canShow) {
            point.show();
          }

          self._handleLegendClick(valuesContext);
        }
      };
    }

    private _getOriginalSeriesEntryForSeriesIndex(seriesIndex) {
      if (this._seriesDataUsed && (seriesIndex < this._seriesDataUsed.seriesList.length)) {
        return this._seriesDataUsed.seriesList[seriesIndex];
      }

      return null;
    }

    private _getChartStateToUse(): TableAsChartState {
      let chartStateToUse: TableAsChartState;
      if (this._previewChartState) {
        chartStateToUse = this._previewChartState;
      }
      else {
        chartStateToUse = this._chartState;
      }

      return chartStateToUse;
    }

    private _getOriginalSeriesConfigForOriginalSeriesEntry(originalSeriesEntry) {
      let chartStateToUse = this._getChartStateToUse();
      if (originalSeriesEntry && Solovis.isNotNull(originalSeriesEntry._seriesExportIndex)) {
        if (originalSeriesEntry._seriesExportIndex < chartStateToUse.seriesExportList.length) {
          // This will be the original definition of the series from chart configuration.
          let originalChartState = chartStateToUse.seriesExportList[originalSeriesEntry._seriesExportIndex];
          return originalChartState;
        }
      }

      return null;
    }

    private _handlePointClick(valuesContext) {
      const self = this;

      function getRowChildrenGroupIds(row: { _nestedGroupKey: string, _rows: Array<any> }) {
        let groupIds = [row._nestedGroupKey];
        groupIds = [...groupIds, ...row._rows.reduce((acc, row) => row._nestedGroupKey ? [...acc, ...getRowChildrenGroupIds(row)] : acc, [] as Array<string>)];
        return groupIds;
      }

      function showChildren(chart, groupKey, argumentName) {
        //find flattened by nested group key to see if there are children that would be needed to be added to the row filter
        const row = chart.tableControl.dataView.flattenedRows.filter(item => {
          return item.nestedGroupKey === TableAsChartControl.AllPrefix + groupKey
        })[0];

        if (row && row.row && row.row._backingRow && row.row._backingRow.fullParentGroupNodeId && row.row._rows && row.row._rows.length > 0) {
          chart.drillDownOrder = [...chart.drillDownOrder, { argumentName, groupKey }];
          updateRowFilter(chart, groupKey);
        }
      }
      function updateRowFilter(chart, groupKey) {
        // get all of the non filtered rows
        const allRowsGrouped = chart.tableControl.groupManager.groupAndAggregate(chart.tableControl.dataView.rows).processedRows;
        // flatten all of the rows
        const flattenedRows = DataTableHelpers.TableModel.flattenVisibleRows(allRowsGrouped, {
          aggregateRowDisplay: chart.tableControl._aggregateRow.display,
          tableSettingsToUse: chart.tableControl._tableSettingsToUse,

          isExpandedCallback: function (nestedGroupKey) {
            return chart.tableControl.expansionManager.isExpanded(nestedGroupKey);
          }
        });
        const row = flattenedRows.filter(item => {
          return item.nestedGroupKey === TableAsChartControl.AllPrefix + groupKey
        })[0];

        if (row && row.row && row.row._rows && row.row._rows.length > 0) {
          chart.filterGroupKeys = getRowChildrenGroupIds(row.row);
          //set up row filter to only show the selected children
          chart.tableControl.setRowFilter(row => {
            return chart.filterGroupKeys.indexOf(TableAsChartControl.AllPrefix + row.fullParentGroupNodeId) !== -1;
          });

          //expand the correct rows
          chart.tableControl.expansionManager.collapseAll();
          chart.tableControl.expansionManager.expand(TableAsChartControl.AllPrefix + groupKey);
          let parent = chart.tableControl.dataView.rows.filter((item) => item.fullGroupNodeId === groupKey)[0];
          while (parent && parent.fullGroupNodeId) {
            chart.tableControl.expansionManager.expand(TableAsChartControl.AllPrefix + parent.fullGroupNodeId);
            parent = chart.tableControl.dataView.rows.filter((item) => item.fullGroupNodeId === parent.fullParentGroupNodeId)[0];
          }

          addBackButton(chart, groupKey);
        }
      }

      function showParents(chart, argument) {
        chart.drillDownOrder.pop();
        if (chart.drillDownOrder.length) {
          const parent = chart.drillDownOrder[chart.drillDownOrder.length - 1];
          updateRowFilter(chart, parent.groupKey);
        } else {
          const parentRow = chart.tableControl.dataView.flattenedRows.filter(row => row.parentRow === null)[0];
          updateRowFilter(chart, parentRow.row._groupKey);
          $(chart._backButton).dxButton({
            icon: "chevronleft",
            visible: false
          }).dxButton("instance");
        }
      }

      function addBackButton(chart, argument) {
        $(chart._backText).text(chart.drillDownOrder.map(item => item.argumentName).join(" - "));
        var backButton = $(chart._backButton).dxButton({
          icon: "chevronleft",
          visible: true,
          onClick: function (e) {
            showParents(chart, argument);
          }
        }).dxButton("instance");

        $(document).on('click', '.undo-drilldown', function () {
          backButton.option("visible", false);
          self._addPointerCursor(self.container);
        });
      }

      if (Boolean((Solovis as any).Info.userEligibleForChartDrillDown)) {
        showChildren(this, valuesContext.groupKey, valuesContext.argument);
      }
    }

    private _addPointerCursor(container) {
      container.addClass('pointer-on-bars');
    }

    private _removePointerCursor(container) {
      container.removeClass('pointer-on-bars');
    }


    private _handleLegendClick(valuesContext) {
      
    }


    public _evaluateCustomTextFunction(formula: string, literalUnlessStartsWithEquals: boolean, values?: any) {
      // NOTE: We previously used text evaluation everywhere, but now, we need to sometimes evaluate and not resolve to strings.
      let value = this._evaluateCustomFunction(formula, literalUnlessStartsWithEquals, values);
      return Solovis.isNotNull(value) ? value.toString() : null;
    }

    public _evaluateCustomFunction(formula: string, literalUnlessStartsWithEquals: boolean, values?: any) {
      try {
        let formulaToUse = formula;
        if (formulaToUse) {
          formulaToUse = formulaToUse.trim();
        }

        if (literalUnlessStartsWithEquals) {
          if (formulaToUse && formulaToUse[0] === '=') {
            formulaToUse = formulaToUse.slice(1);
          }
          else {
            return formula;
          }
        }

        var tableParameters = this.tableControl.parameters;
        var formulaTree = jsep(formulaToUse);

        var value = Solovis.Reports.Parameters.ParameterTransforms.transform(
          null,
          {
            transformTree: formulaTree,

            parameterValueGetter: function (paramName) {
              if (tableParameters)
                return tableParameters[paramName];

              return null;
            },

            externalValues: values
          });

        return value;
      }
      catch (ex) {
        console.log("TableAsChartControl: formula evaluation failed: " + formula);
        console.log(ex);
        return "ERROR - See browser console"
      }
    }


    public _showContextMenu(mouseEvent) {
      let self = this;
      let links = [];

      links.push({
        label: "Chart Options",
        click: function () {
          self.showChartOptionsDialog();
        }
      });

      if (this.tableControl.isTableHidden()) {
        links.push({
          label: "Show Table",
          click: function () {
            self.tableControl._showTable(true);
          }
        });
      }
      else {
        links.push({
          label: "Hide Table",
          click: function () {
            self.tableControl._showTable(false);
          }
        });
      }

      if (this.newControlsSupported) {
        links.push({
          label: "Hide Chart",
          click: function () {
            self._hideChartFromTableControl();
          }
        });
      } else {
        links.push({
          label: "Remove Chart",
          click: function () {
            self._removeChartFromTableControl();
          }
        });
      }

      Solovis.UI.PopupLinkPanel.showLinks({
        newUIEnabled: this.newControlsSupported,
        links: links,
        offset: { pageX: mouseEvent.pageX, pageY: mouseEvent.pageY }
      });
    }

    public _removeChartFromTableControl() {
      this.tableControl._removeChart();

      if (this.tableControl.isTableHidden()) {
        this.tableControl._showTable(true);
      }
    }

    public _hideChartFromTableControl() {
      this.tableControl._hideChart();

      if (this.tableControl.isTableHidden()) {
        this.tableControl._showTable(true);
      }
    }

    public getChartState() {
      let chartStateCopy = Solovis.deepCopy(this._chartState);
      return chartStateCopy;
    }

    public showChartOptionsDialog() {
      Solovis.ModuleLoader.loadModuleByName("Solovis.Charts", () => {
        if (!this._optionsDialog) {
          this._optionsDialog = new (Solovis as any).Charts.Config.TableAsChartConfigDialog({
            tableAsChartControl: this,
            saveCallback: (newChartState) => {
              this._optionsDialog = null;
              this._previewChartState = null;
              this._chartState = newChartState;
              this._updateChart();
            },
            previewCallback: (previewChartState) => {
              this._previewChartState = previewChartState;
              this._updateChart();
            },
            cancelCallback: () => {
              this._optionsDialog = null;
              if (this._previewChartState) {
                this._previewChartState = null;
                this._updateChart();
              }
            }
          });

          this._optionsDialog.show();
        }
      });
    }

    public showAddChartDialog() {
      Solovis.ModuleLoader.loadModuleByName("Solovis.Charts", () => {
        let addChartDialog = new (Solovis as any).Charts.Config.AddChartDialog({
          tableAsChartControl: this,
          saveCallback: (newChartState) => {
            this._previewChartState = null;
            this._chartState = newChartState;
            this._updateChart();
            Solovis.UX.Utils.emit("@solovis/report/chart-created");
            Solovis.UX.Utils.emit("@solovis/report/chart-shown");
          },
          previewCallback: (previewChartState) => {
            this._previewChartState = previewChartState;
            this._updateChart();
          },
          cancelCallback: () => {
            // We wil just remove the chart when canceling out of Add Chart step.             
            this._removeChartFromTableControl();
          }
        });

        addChartDialog.show();
      });
    }
  }


  export class PointPaletteProcessor {
    private _palette: any;
    private _tableControl;
    private _chartType;

    private _entryMatchList: any[];
    private _entryShiftList: any[];

    constructor(options) {
      let self = this;

      this._palette = options.palette;
      this._tableControl = options.tableControl;
      this._chartType = options.chartType;

      this._entryMatchList = [];
      this._entryShiftList = [];
      if (this._palette && this._palette.entries) {
        for (let paletteEntry of this._palette.entries) {
          let entry: any = {
            color: paletteEntry.color,
            match: paletteEntry.match
          };

          let matchStr = paletteEntry.match;
          if (matchStr) {
            if (matchStr[0] == '=') {
              // Match an expression. 
              matchStr = matchStr.slice(1);
              let formulaTree = jsep(matchStr);

              entry.isMatch = function (valuesContext) {
                var tableParameters = self._tableControl.parameters;

                var matchValue = Solovis.Reports.Parameters.ParameterTransforms.transform(
                  null,
                  {
                    transformTree: formulaTree,

                    parameterValueGetter: function (paramName) {
                      if (tableParameters)
                        return tableParameters[paramName];

                      return null;
                    },

                    externalValues: valuesContext
                  });

                return !!matchValue;
              };
            }
            else {
              entry.isMatch = this._getIsMatch(matchStr);
            }

            this._entryMatchList.push(entry);
          } else {
            //Entry without a match specified is pushed to the entry shift list.
            this._entryShiftList.push(entry);
          }
        }
      }
    }

    public addToMatchList(color, matchStr) {
      const entry: any = {
        color: color,
        match: matchStr
      };

      entry.isMatch = this._getIsMatch(matchStr);

      let entryIndex;
      const alreadyExists = this._entryMatchList.some(function (entry, index) { entryIndex = index; return entry.match === matchStr; });

      if (alreadyExists) {
        this._entryMatchList[entryIndex].color = color;
      }
      else {
        this._entryMatchList.push(entry);
      }
    }

    public checkForColor(valuesContext: any) {
      try {
        if (valuesContext) {
          for (let entry of this._entryMatchList) {
            if (entry.isMatch && entry.isMatch(valuesContext)) {
              return entry.color;
            }
          }

          let matchStr;
          if (this._chartType == "pie") {
            matchStr = typeof valuesContext.argument === 'string' ? valuesContext.argument : valuesContext.argumentText;
          } else {
            matchStr = valuesContext.seriesName;
          }

          if (typeof matchStr !== 'string') return false;
          //Shift and return the first color for non-match series if there are still colors left to shift.
          if (matchStr != null && this._entryShiftList.length > 0) {
            let entry = this._entryShiftList.shift();
            entry.isMatch = this._getIsMatch(matchStr);
            //Promote to entry match list.
            this._entryMatchList.push(entry);
            return entry.color;
          }
        }

        return null;
      }
      catch (ex) {
        console.log("ERROR: PointPaletteProcessor could not process match.");
        console.log(ex);
        return null;
      }
    }

    private _getIsMatch(match) {
      const matchStr = !match || typeof match === "string" ? match : match.toString();
      //Sanity check that no undefined match strings or default object conversion happened
      if (!matchStr || matchStr.indexOf("[object") === 0) {
        throw Error();
      }

      const strToMatch = matchStr.trim().toLowerCase();
      return (valuesContext) => {
        const compareMatch = [
          valuesContext.argument,
          valuesContext.argumentText,
          valuesContext.seriesName
        ]
          .filter(argument => typeof argument === "string")
          .map<string>(argument => argument.trim().toLowerCase())

        return compareMatch.indexOf(strToMatch) !== -1;
      }
    }
  }
}